import {
    MarginCssStyleDeclarations,
    MarginCssStyleDeclarationsAsString
} from '../components/action-item/entities/margin-style.type';

// How the following helper functions work in fixed positioning scenario
// The styles properties are always the distance to its border
// -> style.right = distrance from the element to the right of the screen
// -> style.bottom = distance from the element to the bottom of the screen

// DOMRect information is a little different
// DOMRect.right is the distance from the left side of the screen to the right side of the targetted element

// example: child.style.right = `${viewportWidth() - (parentRect?.left ?? 0)}px`;
// The right side of the child is aligned to the left side using this trick
// A style.right of the full viewport width moves the element all the way to the left, off the page; subtracting the
// target's own x brings it back to the right, aligning it with the target.
// The width subtracted from must be the one `right` is resolved against — see viewportWidth() at the bottom.

export function fixElementTopToParentTop(child: HTMLElement, parentRect: DOMRect | undefined): void {
    const min = 0;
    const max: number = unobstructedViewportBottom(parentRect) - child.getBoundingClientRect().height;

    const repositionValue: number = repositionFixedDomElement(
        min,
        max,
        parentRect?.top,
        'marginTop',
        'margin-top',
        child
    );
    child.style.top = repositionValue + 'px';
}

export function fixElementTopToParentBottom(child: HTMLElement, parentRect: DOMRect | undefined): void {
    const min = 0;
    const max: number = unobstructedViewportBottom(parentRect) - child.getBoundingClientRect().height;

    const repositionedValue: number = repositionFixedDomElement(
        min,
        max,
        parentRect?.bottom,
        'marginTop',
        'margin-top',
        child
    );
    child.style.top = `${repositionedValue}px`;
}

export function fixElementBottomToParentBottom(child: HTMLElement, parentRect: DOMRect | undefined): void {
    const min: number = child.getBoundingClientRect().height;
    const max: number = unobstructedViewportBottom(parentRect);

    const repositionValue: number = repositionFixedDomElement(
        min,
        max,
        parentRect?.bottom,
        'marginBottom',
        'margin-bottom',
        child
    );
    child.style.bottom = `calc(100vh - ${repositionValue}px)`;
}

export function fixElementBottomToParentTop(child: HTMLElement, parentRect: DOMRect | undefined): void {
    const min: number = child.getBoundingClientRect().height;
    const max: number = unobstructedViewportBottom(parentRect);

    const repositionedValue: number = repositionFixedDomElement(
        min,
        max,
        parentRect?.top,
        'marginBottom',
        'margin-bottom',
        child
    );
    child.style.bottom = `calc(100vh - ${repositionedValue}px)`;
}

export function fixElementLeftToParentLeft(child: HTMLElement, parentRect: DOMRect | undefined): void {
    const min = 0;
    const max: number = document.body.scrollWidth - child.getBoundingClientRect().width;

    const repositionValue: number = repositionFixedDomElement(
        min,
        max,
        parentRect?.left ?? 0,
        'marginLeft',
        'margin-left',
        child
    );
    child.style.left = repositionValue + 'px';
}

export function fixElementLeftToParentRight(child: HTMLElement, parentRect: DOMRect | undefined): void {
    const min = 0;
    const max: number = document.body.scrollWidth - child.getBoundingClientRect().width;

    const repositionValue: number = repositionFixedDomElement(
        min,
        max,
        parentRect?.right ?? 0,
        'marginLeft',
        'margin-left',
        child
    );
    child.style.left = repositionValue + 'px';
}

export function fixElementRightToParentLeft(child: HTMLElement, parentRect: DOMRect | undefined): void {
    const min: number = child.getBoundingClientRect().width;
    const viewport: number = viewportWidth();

    const repositionValue: number = repositionFixedDomElement(
        min,
        viewport,
        parentRect?.left,
        'marginRight',
        'margin-right',
        child
    );
    child.style.right = `${viewport - repositionValue}px`;
}

export function fixElementRightToParentRight(child: HTMLElement, parentRect: DOMRect | undefined): void {
    const min: number = child.getBoundingClientRect().width;
    const viewport: number = viewportWidth();

    const repositionedValue: number = repositionFixedDomElement(
        min,
        viewport,
        parentRect?.right,
        'marginRight',
        'margin-right',
        child
    );
    child.style.right = `${viewport - repositionedValue}px`;
}

/**
 * The width a `position: fixed` element's `right` is measured against: the initial containing block, which is the
 * viewport minus a classic scrollbar.
 *
 * ⚠ Not `document.body.scrollWidth`, which is what this file measured against before. The two agree only while the
 * page does not overflow sideways — and the record overviews do, by design: `.side-by-side` pulls itself left with
 * a negative margin, so its right edge runs past the viewport and `body.scrollWidth` exceeds the viewport by that
 * much. Every right-anchored panel on those pages was then placed that many pixels too far left, which walked the
 * leftmost ones (the bewaarde weergaven menus, in a 300px filter column at the page's left edge) off screen.
 *
 * `100vw` was the other half of the same mistake: it *includes* the scrollbar, so it needed a correction term that
 * itself assumed no horizontal overflow. `clientWidth` needs no correction at all.
 */
function viewportWidth(): number {
    return document.documentElement.clientWidth;
}

const FOOT_PINNED_BAR_SELECTOR = '.sh-page-template__main__actions, .sh-hierarchy-table-stack__bars';

function unobstructedViewportBottom(toggleRect: DOMRect | undefined): number {
    const viewportHeight: number = window.innerHeight;
    let bottom: number = viewportHeight;

    document.querySelectorAll<HTMLElement>(FOOT_PINNED_BAR_SELECTOR).forEach((bar: HTMLElement) => {
        const barRect: DOMRect = bar.getBoundingClientRect();
        const isPinnedToTheFoot: boolean = barRect.height > 0 && barRect.bottom >= viewportHeight - 1;

        if (isPinnedToTheFoot && isUnderTheToggle(barRect, toggleRect)) {
            bottom = Math.min(bottom, barRect.top);
        }
    });

    return bottom;
}

function isUnderTheToggle(barRect: DOMRect, toggleRect: DOMRect | undefined): boolean {
    if (!toggleRect) {
        return true;
    }

    return toggleRect.left < barRect.right && toggleRect.right > barRect.left;
}

function repositionFixedDomElement(
    min: number,
    max: number,
    wish: number = 0,
    cssDeclarationToClear: MarginCssStyleDeclarations,
    cssDeclarationAsString: MarginCssStyleDeclarationsAsString,
    element: HTMLElement
): number {
    let sanitizeWishValue: number = wish;

    if (sanitizeWishValue < min) {
        sanitizeWishValue = min;
    } else if (sanitizeWishValue > max) {
        sanitizeWishValue = max;
    }

    if (isValueAtBoundaries(sanitizeWishValue, min, max)) {
        element.style[cssDeclarationToClear] = '0';
    } else {
        element.style.removeProperty(cssDeclarationAsString);
    }

    return sanitizeWishValue;
}

function isValueAtBoundaries(actualValue: number, minValue: number, maxValue: number): boolean {
    return actualValue === minValue || actualValue === maxValue;
}
